import type { ToolApprovalCardProps } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { EditErrors, FieldGroup, textEditor } from "#features/conversations/components/prepared-action/cardControls.tsx";
import { DraftTextArea } from "#features/conversations/components/prepared-action/DraftTextArea.tsx";
import { endsNotAfterStart, moveEvent, providerOf, wallClockParts } from "#features/conversations/components/prepared-action/calendarEventFields.ts";
import { readBoolean, readString, type Fields } from "#features/conversations/lib/untypedFields.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { DatePicker, Switch, TextField } from "@hopper-ui/components";
import { useEffect, useRef, type ReactNode } from "react";

interface Props extends ToolApprovalCardProps {
    isDisabled: boolean;
    focusesTitle: boolean;
    // The rows between the two fields: the manager rewords the title and description and turns the online meeting on or off; the rest stays text.
    children: ReactNode;
}

// Only these fields are on screen, so an error for any other shows at the card's end. A batch row has no time inputs.
export const EDITABLE_FIELDS = ["subject", "description"];
export const TIME_FIELDS = ["start", "end"];

interface OnlineMeetingSwitchProps {
    // The call as it will be booked, edits included.
    args: Fields;
    isDisabled: boolean;
    onChange: (isOnline: boolean) => void;
}

export function OnlineMeetingSwitch({ args, isDisabled, onChange }: OnlineMeetingSwitchProps) {
    const { tShared } = useSharedTranslation();
    const provider = providerOf(args);
    const labelKey = provider === "outlook" ? "calendarEvent.onlineSwitchOutlook" : provider === "google" ? "calendarEvent.onlineSwitchGoogle" : "calendarEvent.onlineSwitch";

    return (
        <Switch isSelected={readBoolean(args, "is_online_event", true)} onChange={onChange} isDisabled={isDisabled} size="sm">
            {tShared(labelKey)}
        </Switch>
    );
}

interface FieldProps extends ToolApprovalCardProps {
    isDisabled: boolean;
}

export function TitleField({ isDisabled, focusesOnMount = false, ...props }: FieldProps & { focusesOnMount?: boolean }) {
    const { tShared } = useSharedTranslation();
    const errors = props.editErrors.filter(error => error.field === "subject");
    const editor = textEditor(props, "subject");
    const inputRef = useRef<HTMLInputElement | null>(null);
    // A plain focus, not autoFocus: React Aria focuses without scrolling, so a title above the fold would take focus out of view.
    useEffect(() => {
        if (focusesOnMount) {
            inputRef.current?.focus();
        }
    }, [focusesOnMount]);

    return (
        <FieldGroup label={tShared("calendarEvent.title")}>
            {labelId => (
                <>
                    <TextField
                        aria-labelledby={labelId}
                        inputRef={inputRef}
                        value={editor.value}
                        onChange={editor.onChange}
                        isInvalid={errors.length > 0}
                        isDisabled={isDisabled}
                        isFluid
                        className="wai-pa-field wai-pa-input"
                    />
                    <EditErrors errors={errors} />
                </>
            )}
        </FieldGroup>
    );
}

interface TimeInputProps {
    label: string;
    value: string;
    isInvalid: boolean;
    isDisabled: boolean;
    onChange: (value: string) => void;
}

// What a native time input hands back; a half-typed value is no value to book.
const TIME_VALUE = /^\d{2}:\d{2}$/;

function TimeInput({ label, value, isInvalid, isDisabled, onChange }: TimeInputProps) {
    return (
        <div className="wai-ce-when-field">
            <FieldGroup label={label}>
                {labelId => (
                    <TextField
                        type="time"
                        aria-labelledby={labelId}
                        value={value}
                        // A cleared or unreadable input has no value to book, so the last one stays.
                        onChange={next => TIME_VALUE.test(next) && onChange(next)}
                        isInvalid={isInvalid}
                        isDisabled={isDisabled}
                        isFluid
                        className="wai-pa-field wai-pa-input"
                    />
                )}
            </FieldGroup>
        </div>
    );
}

interface DateInputProps {
    label: string;
    value: string;
    isDisabled: boolean;
    onChange: (value: string) => void;
}

function DateInput({ label, value, isDisabled, onChange }: DateInputProps) {
    return (
        <div className="wai-ce-when-field">
            <FieldGroup label={label}>
                {labelId => (
                    <DatePicker
                        aria-labelledby={labelId}
                        value={parseDate(value)}
                        // A cleared date has no value to book, so the last one stays.
                        onChange={next => next !== null && onChange(next.toString())}
                        minValue={today(getLocalTimeZone())}
                        isDisabled={isDisabled}
                        isFluid
                        className="wai-pa-field wai-pa-input"
                    />
                )}
            </FieldGroup>
        </div>
    );
}

// The time row in Edit mode: one date for the event, and its start and end. Each edit writes the full wall-clock value the tool takes.
export function WhenFields({ isDisabled, ...props }: FieldProps) {
    const { tShared } = useSharedTranslation();
    const shown = { ...props.args, ...props.edits };
    const start = wallClockParts(readString(shown, "start"));
    const end = wallClockParts(readString(shown, "end"));
    if (start === null || end === null) {
        return null;
    }
    // A new date or start moves the whole event, and a new end lands on the start's day. An end that is not after the start is no event,
    // and core reports that for the whole call, so the card checks it where the manager is typing.
    const isRangeEmpty = endsNotAfterStart(start, end);
    const errors = props.editErrors.filter(error => error.field !== null && TIME_FIELDS.includes(error.field));
    const isInvalid = (field: string) => errors.some(error => error.field === field);

    return (
        <>
            <div className="wai-ce-when-fields">
                <DateInput label={tShared("calendarEvent.date")} value={start.date} isDisabled={isDisabled} onChange={date => props.onEditArgs(moveEvent(start, end, { ...start, date }))} />
                <TimeInput label={tShared("calendarEvent.startTime")} value={start.time} isInvalid={isInvalid("start")} isDisabled={isDisabled} onChange={time => props.onEditArgs(moveEvent(start, end, { ...start, time }))} />
                <TimeInput label={tShared("calendarEvent.endTime")} value={end.time} isInvalid={isInvalid("end") || isRangeEmpty} isDisabled={isDisabled} onChange={time => props.onEditArgs({ end: `${start.date}T${time}:00` })} />
            </div>
            <EditErrors errors={isRangeEmpty ? [...errors, { field: "end", message: tShared("calendarEvent.endNotAfterStart") }] : errors} />
        </>
    );
}

export function DescriptionField({ isDisabled, ...props }: FieldProps) {
    const { tShared } = useSharedTranslation();
    const errors = props.editErrors.filter(error => error.field === "description");

    return (
        <div className="wai-pa-field-group">
            <DraftTextArea
                editor={textEditor(props, "description")}
                label={tShared("calendarEvent.description")}
                isInvalid={errors.length > 0}
                isDisabled={isDisabled}
                isLabelVisible
            />
            <EditErrors errors={errors} />
        </div>
    );
}

export function CalendarEventEditor({ isDisabled, focusesTitle, children, ...props }: Props) {
    return (
        <>
            <TitleField {...props} isDisabled={isDisabled} focusesOnMount={focusesTitle} />
            {children}
            <DescriptionField {...props} isDisabled={isDisabled} />
        </>
    );
}
